import { Loader } from "@googlemaps/js-api-loader";

export default class StaticLocationMap {
    private lat: number;
    private lng: number;
    private locationName: string;
    private defaultZoom: number;
    private mapStyle: string | null;
    private googleMap: google.maps.Map | null = null;
    private mapElement: HTMLDivElement;
    AdvancedMarkerElement: typeof google.maps.marker.AdvancedMarkerElement;
    InfoWindow: typeof google.maps.InfoWindow;
    ControlPosition: typeof google.maps.ControlPosition;
    showInfoWindow: boolean

    constructor(map: HTMLDivElement, locationName: string, lat: number, lng: number, defaultZoom: number = 12, private apiKey: string, showInfoWindow: boolean = false) {
        this.lat = lat;
        this.lng = lng;
        this.locationName = locationName;
        this.defaultZoom = defaultZoom;
        this.mapStyle = null;
        this.mapElement = map;
        this.showInfoWindow = showInfoWindow

        this.init()
    }

    // Init map elements
    public async init() {
        this.lat = Number(this.mapElement.getAttribute("data-latitude"));
        this.lng = Number(this.mapElement.getAttribute("data-longitude"));

        this.googleMap = await this.initiateMap(this.mapElement);
        if (this.googleMap) {
            const controlButton = this.createCustomControlButton();
            this.googleMap.controls[this.ControlPosition.INLINE_END_BLOCK_END].push(controlButton);
            this.placeMarker();
            this.setIframeTitle();
        }
    }

    //Initialize the map without filters or listing functionality
    public async initiateMap(mapElement: HTMLDivElement): Promise<google.maps.Map | null> {
        try {
            if (!mapElement) {
                return null;
            }

            const loader = new Loader({
                apiKey: this.apiKey
            });

            const { Map, MapTypeId, InfoWindow } = await loader.importLibrary("maps") as google.maps.MapsLibrary;
            const { ControlPosition } = await loader.importLibrary("core") as google.maps.CoreLibrary;
            const { AdvancedMarkerElement } = await loader.importLibrary("marker") as google.maps.MarkerLibrary;

            this.AdvancedMarkerElement = AdvancedMarkerElement;
            this.InfoWindow = InfoWindow;
            this.ControlPosition = ControlPosition;
            this.mapStyle = mapElement.getAttribute("data-mapId");

            const coordinates: google.maps.LatLngLiteral = {
                lat: this.lat,
                lng: this.lng
            };

            const googleMap = new Map(mapElement, {
                mapId: this.mapStyle || MapTypeId.ROADMAP,
                center: coordinates,
                zoom: this.defaultZoom,
                disableDefaultUI: true,
                zoomControl: true,
                gestureHandling: 'cooperative',    
            } as google.maps.MapOptions
            );

            return googleMap;
        } catch (err) {
            console.error(err);
            return null;
        }
    }

    private setClientLocation() {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition((position) => {
                const pos = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                }
                this.googleMap.setCenter(pos);
            });
        }
    }

    private createCustomControlButton() {
        const controlButton = document.createElement('button') as HTMLButtonElement;
        controlButton.classList.add('compass-button');
        controlButton.type = "button";
        controlButton.style.backgroundColor = '#fff';
        controlButton.style.border = '2px solid #fff';
        controlButton.style.borderRadius = '4px';
        controlButton.style.boxShadow = '0 2px 6px rgba(0,0,0,.3)';
        controlButton.innerHTML = "<span class='visually-hidden'>Use my location</span><img alt='' aria-hidden='true' src='/assets/media/crosshairs-icon.svg' class='icon'/>"
        controlButton.addEventListener('click', () => this.setClientLocation());
        return controlButton;
    }

    private placeMarker() {
        const infoWindow = new this.InfoWindow();
        const isServiceWA = this.locationName.toLowerCase().includes("servicewa");

        const dotLogoImg = document.createElement('img');
        dotLogoImg.src = isServiceWA ? "/assets/media/servicewa-logo.svg" : "/assets/media/dot-marker-logo.svg";
        dotLogoImg.classList.add("dot-marker");
        if (isServiceWA) {
            dotLogoImg.classList.add("service-wa");
        }
        let infoWindowContent =
            `<h4 class="bold-label--std">${this.locationName}</h4>
        <div class="info--address">
            <a href="https://www.google.com/maps/dir/?api=1&destination=${this.lat},${this.lng}" target="_blank">Get Directions</a>
        </div>`;

        const marker = new this.AdvancedMarkerElement({
            position: new google.maps.LatLng(this.lat, this.lng),
            content: dotLogoImg,
            map: this.googleMap,
            title: this.locationName,
            gmpClickable: this.showInfoWindow ? true : false,

        });
        if (this.showInfoWindow) {
            marker.addListener('click', () => {
                infoWindow.setContent(infoWindowContent);
                infoWindow.open(this.googleMap, marker);
            });
        }
    }

    private setIframeTitle(): void {
        google.maps.event.addListenerOnce(this.googleMap, 'idle', () => {
            const iframe: HTMLIFrameElement = this.mapElement.getElementsByTagName('iframe')[0];
            iframe.title = "Google Maps";
        });
    }
}
